import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { Secao, Vazio, Carregando, Erro, Indicador, Indicadores } from '../componentes/ui'
import './conta-seguranca.css'

/**
 * Conta e segurança.
 *
 * ─── TRÊS PERGUNTAS, NESTA ORDEM ───────────────────────────────────
 *
 * 1. Alguém entrou na minha conta? → sessões abertas, com o botão de
 *    derrubar. É o que a pessoa procura quando desconfia, e vem antes
 *    de tudo porque é o único item urgente da tela.
 * 2. A trilha está inteira? → a corrente de hash verificada de verdade,
 *    com o resultado dito sem eufemismo.
 * 3. Quem olhou o quê? → os últimos eventos, com filtro por paciente.
 *
 * O plano vem por último. É informação de cobrança, não de segurança, e
 * abrir esta tela num susto e encontrar "seu plano é o Essencial" é o
 * tipo de hierarquia que faz alguém desistir de procurar.
 *
 * ─── SOBRE DIZER "ÍNTEGRA" ─────────────────────────────────────────
 *
 * A verificação não detecta a remoção da ÚLTIMA linha da trilha —
 * nenhum elo aponta para ela. A tela mostra o hash da cabeça e diz para
 * anotá-lo fora do sistema, porque é isso que fecha a brecha. Escrever
 * "trilha inviolável" e calar sobre o limite seria vender garantia que
 * não existe.
 */

type Sessao = {
  id: string; clinica: string | null
  criado_em: string; expira_em: string
  ip: string | null; user_agent: string | null
}

type Problema = {
  problema: string; linha_id: string
  ocorrido_em: string; detalhe: string
}

type Integridade = {
  integra: boolean; problemas: Problema[]
  cabeca: string | null; ponta: number | null
  linhas: number; ate: string | null; limite: string
}

type Evento = {
  id: string; n: string; ocorrido_em: string; evento: string
  recurso_tipo: string | null; paciente_id: string | null
  quem: string | null; papel: string | null; ip: string | null
}

type Plano = {
  plano: string; estado: string; valor_centavos: string
  profissionais: number; max_profissionais: number | null
  usuarios: number; max_usuarios: number | null
  pacientes: number; max_pacientes: number | null
  dias_de_teste_restantes: number | null
}

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR',
    { day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit' })

const brl = (c: string) =>
  (Number(c) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * "Chrome no Windows" em vez da string crua de 140 caracteres.
 * Aproximação de propósito: o objetivo é a pessoa reconhecer o próprio
 * aparelho, não identificar o build do navegador.
 */
function aparelho(ua: string | null): string {
  if (!ua) return 'aparelho desconhecido'
  const nav = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari' : 'navegador'
  const so = /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iPhone ou iPad'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'Mac'
    : /Linux/.test(ua) ? 'Linux' : 'sistema desconhecido'
  return `${nav} no ${so}`
}

const ROTULO: Record<string, string> = {
  'prontuario.lido': 'abriu o prontuário',
  'ficha.assinada': 'assinou uma ficha',
  'ficha.adendo': 'registrou um adendo',
  'trilha.consultada': 'consultou a trilha',
  'acesso.consultado': 'consultou os acessos de um paciente',
  'sessao.encerrada': 'encerrou uma sessão',
  'sessao.encerrada_todas': 'encerrou as outras sessões',
}

/**
 * `foco` deixa o mesmo componente servir a dois destinos da barra
 * lateral. A trilha saiu de dentro de "Conta e segurança" e virou
 * destino próprio porque ela responde a outra pergunta e a outra
 * pessoa: conta é de quem administra, trilha é de quem audita — e o
 * escopo `auditoria:read` existe justamente para separar os dois.
 */
export function ContaSeguranca({ foco = 'tudo' }: {
  foco?: 'tudo' | 'conta' | 'trilha'
} = {}) {
  const { pode } = useSessao()
  const verTrilha = pode('auditoria:read')

  const [sessoes, setSessoes] = useState<Sessao[]>([])
  const [atual, setAtual] = useState<string | null>(null)
  const [integridade, setIntegridade] = useState<Integridade | null>(null)
  const [eventos, setEventos] = useState<Evento[]>([])
  const [plano, setPlano] = useState<Plano | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const [s, p] = await Promise.all([
        api<{ itens: Sessao[]; atual: string }>('/v1/sessoes'),
        api<Plano | null>('/v1/meu-plano'),
      ])
      setSessoes(s.itens); setAtual(s.atual); setPlano(p)

      // A trilha é só de quem tem `auditoria:read`. Pedir e engolir o
      // 404 funcionaria, mas deixaria a tela piscando um erro que não é
      // erro — a pessoa simplesmente não tem esse acesso.
      if (verTrilha) {
        const [i, t] = await Promise.all([
          api<Integridade>('/v1/trilha/integridade'),
          api<{ itens: Evento[] }>('/v1/trilha?limite=50'),
        ])
        setIntegridade(i); setEventos(t.itens)
      }
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [verTrilha])

  useEffect(() => { void carregar() }, [carregar])

  async function encerrar(id: string) {
    setAviso(null)
    try {
      const r = await api<{ encerradas: number }>(`/v1/sessoes/${id}`, { metodo: 'DELETE' })
      setAviso(`${r.encerradas} sessão(ões) encerrada(s).`)
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível encerrar.')
    }
  }

  async function encerrarOutras() {
    setAviso(null)
    try {
      const r = await api<{ encerradas: number }>('/v1/sessoes/encerrar-outras',
                                                 { metodo: 'POST' })
      setAviso(r.encerradas === 0
        ? 'Não havia outra sessão aberta.'
        : `${r.encerradas} sessão(ões) encerrada(s). Este aparelho continua conectado.`)
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível encerrar.')
    }
  }

  if (carregando) return <Carregando altura={240} />

  const outras = sessoes.filter((s) => s.id !== atual)

  return (
    <>
      <Erro mensagem={erro} />
      {aviso && <div className="aviso aviso--ok" role="status">{aviso}</div>}

      {/* ── 1 · quem está na minha conta ─────────────────────────── */}
      {foco !== 'trilha' && <Secao
        titulo="Aparelhos conectados"
        descricao="Onde a sua conta está aberta agora. Se você não reconhece algum, encerre e troque a senha."
        acao={outras.length > 0 && (
          <button className="botao botao--fantasma" onClick={encerrarOutras}>
            Sair dos outros {outras.length === 1 ? 'aparelhos' : `${outras.length} aparelhos`}
          </button>
        )}
      >
        <ul className="cs-sessoes">
          {sessoes.map((s) => (
            <li key={s.id} className={s.id === atual ? 'is-atual' : ''}>
              <div>
                <strong>
                  {aparelho(s.user_agent)}
                  {s.id === atual && <span className="cs-aqui">este aparelho</span>}
                </strong>
                <span className="ajuda">
                  {s.ip ?? 'sem IP registrado'} · entrou em {dataHora(s.criado_em)}
                  {' · '}expira em {dataHora(s.expira_em)}
                </span>
              </div>
              {/* Derrubar a própria sessão pela lista deslogaria a pessoa
                  sem ela entender por quê. Para sair daqui existe o menu. */}
              {s.id !== atual && (
                <button className="cs-encerrar" onClick={() => encerrar(s.id)}>
                  Encerrar
                </button>
              )}
            </li>
          ))}
        </ul>
      </Secao>}

      {/* ── 2 · a trilha ────────────────────────────────────────── */}
      {foco !== 'conta' && verTrilha && integridade && (
        <Secao
          titulo="A trilha de acesso"
          descricao="Cada evento é encadeado por hash ao anterior. Alterar ou apagar qualquer linha do meio quebra a corrente de forma detectável."
        >
          <div className={`cs-selo ${integridade.integra ? 'is-ok' : 'is-quebrada'}`}>
            <strong>
              {integridade.integra
                ? 'A corrente está inteira'
                : `${integridade.problemas.length} problema(s) na corrente`}
            </strong>
            <span>
              {integridade.linhas.toLocaleString('pt-BR')} eventos registrados
              {integridade.ate && ` · último em ${dataHora(integridade.ate)}`}
            </span>
          </div>

          {!integridade.integra && (
            <ul className="cs-problemas">
              {integridade.problemas.map((p) => (
                <li key={p.linha_id}>
                  <strong>{p.problema.replace(/_/g, ' ').toLowerCase()}</strong>
                  <span>{p.detalhe} · {dataHora(p.ocorrido_em)}</span>
                </li>
              ))}
            </ul>
          )}

          {/* O limite dito onde a pessoa está lendo "está inteira" — e
              não escondido numa página de documentação. */}
          {integridade.cabeca && (
            <div className="cs-ancora">
              <p className="ajuda">{integridade.limite}</p>
              <label htmlFor="cs-cabeca">Hash da cabeça (evento {integridade.ponta})</label>
              <input id="cs-cabeca" className="entrada mono" readOnly
                     value={integridade.cabeca}
                     onFocus={(e) => e.currentTarget.select()} />
            </div>
          )}
        </Secao>
      )}

      {/* ── 3 · quem olhou o quê ────────────────────────────────── */}
      {foco !== 'conta' && verTrilha && (
        <Secao titulo="Últimos eventos"
               descricao="Quem fez o quê, na ordem em que aconteceu.">
          {eventos.length === 0 ? (
            <Vazio mensagem="Nada registrado ainda." />
          ) : (
            <ol className="cs-eventos">
              {eventos.map((e) => (
                <li key={e.id}>
                  <span className="cs-eventos__n mono">#{e.n}</span>
                  <span className="cs-eventos__quando mono">{dataHora(e.ocorrido_em)}</span>
                  <span className="cs-eventos__quem">{e.quem ?? 'sistema'}</span>
                  <span className="cs-eventos__oque">
                    {ROTULO[e.evento] ?? e.evento.replace(/[._]/g, ' ')}
                  </span>
                  {e.paciente_id && <span className="cs-tag">paciente</span>}
                </li>
              ))}
            </ol>
          )}
        </Secao>
      )}

      {foco !== 'conta' && !verTrilha && (
        <Secao titulo="A trilha de acesso"
               descricao="Quem abriu qual prontuário, e quando.">
          <p className="ajuda">
            Só o dono da clínica e o papel Auditor consultam a trilha. É de
            propósito: uma trilha que qualquer pessoa lê deixa de vigiar o
            acesso e passa a vigiar os colegas.
          </p>
        </Secao>
      )}

      {/* ── 4 · o plano, por último ─────────────────────────────── */}
      {foco !== 'trilha' && plano && (
        <Secao titulo="Plano"
               descricao={plano.estado === 'TESTE' && plano.dias_de_teste_restantes !== null
                 ? `Em teste — ${plano.dias_de_teste_restantes} dia(s) restantes.`
                 : `${brl(plano.valor_centavos)} por mês.`}>
          <Indicadores>
            <Indicador rotulo="Plano" valor={plano.plano} />
            <Indicador rotulo="Profissionais"
                       valor={`${plano.profissionais}${plano.max_profissionais ? ` / ${plano.max_profissionais}` : ''}`}
                       ajuda={plano.max_profissionais ? 'do limite do plano' : 'sem limite'} />
            <Indicador rotulo="Usuários"
                       valor={`${plano.usuarios}${plano.max_usuarios ? ` / ${plano.max_usuarios}` : ''}`} />
            <Indicador rotulo="Pacientes"
                       valor={`${plano.pacientes}${plano.max_pacientes ? ` / ${plano.max_pacientes}` : ''}`} />
          </Indicadores>
          <p className="ajuda cs-nota">
            Prontuário, backup e exportação estão em todos os planos. O que muda
            entre eles é o tamanho da clínica, não o que ela pode fazer.
          </p>
          {/* A carência, escrita ANTES de acontecer — é o diferencial: o
              mercado trava no meio do atendimento sem avisar. */}
          <p className="ajuda cs-nota">
            <strong>Se a mensalidade atrasar:</strong> 30 dias de aviso, com tudo
            funcionando. Só depois a conta entra em modo só-leitura — e mesmo aí
            você continua vendo tudo, exportando e concluindo os atendimentos do
            dia. Nunca travamos no meio de um atendimento, e nada é apagado.
          </p>
        </Secao>
      )}

      {/* ── seus dados ──────────────────────────────────────────────
          A home promete "exportação de tudo o que é seu, a qualquer
          momento, sem pedir para nós". Aqui a promessa vira botão. */}
      {foco !== 'trilha' && pode('config:write') && (
        <Secao titulo="Seus dados"
               descricao="Baixe tudo o que é seu — pacientes, prontuário, agenda e financeiro — num arquivo, quando quiser. Sem pedir para a gente, sem taxa de saída.">
          <BaixarDados />
        </Secao>
      )}
    </>
  )
}

/**
 * O botão que cumpre a promessa da saída sem sequestro.
 *
 * Faz o download pelo `fetch` (não por link) para tratar o erro com
 * frase de gente e mostrar que está trabalhando — um export de clínica
 * cheia leva alguns segundos.
 */
function BaixarDados() {
  const [estado, setEstado] = useState<'ocioso' | 'baixando' | 'erro'>('ocioso')

  async function baixar() {
    setEstado('baixando')
    try {
      const resp = await fetch('/v1/exportar', { credentials: 'same-origin' })
      if (!resp.ok) throw new Error(String(resp.status))
      const blob = await resp.blob()
      const cd = resp.headers.get('Content-Disposition') ?? ''
      const nome = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'clinuma-dados.json'
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = nome; document.body.appendChild(a); a.click()
      a.remove(); URL.revokeObjectURL(url)
      setEstado('ocioso')
    } catch {
      setEstado('erro')
    }
  }

  return (
    <div className="cs-export">
      <button className="botao" disabled={estado === 'baixando'} onClick={() => void baixar()}>
        {estado === 'baixando' ? 'Preparando o arquivo…' : 'Baixar todos os meus dados'}
      </button>
      {estado === 'erro' && (
        <p className="aviso aviso--erro" role="alert">
          Não foi possível gerar o arquivo agora. Tente de novo em instantes.
        </p>
      )}
      <p className="ajuda">
        Um arquivo <code>.json</code>, uma tabela por seção. Anexos (arquivos)
        vêm em download separado quando existirem.
      </p>
    </div>
  )
}
